/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';
import { q, num, Kpi } from './kit';
import SubTabs from './SubTabs';
import DetailList from './DetailList';
import { formatUTCDate, formatMedicineLog } from '../../../utils/utils';

const yesNo = (v) => (v ? 'Yes' : 'No');
const dt = (v) => (v ? formatUTCDate(v) : '—');

// Two record types — Medicine log and Medicines (master) — each shown inline as a
// sub-tab list mirroring the standalone /medicine-log-details and /medicine-master-details
// pages, replacing the old redirect-on-click boxes.
const SUBS = [
  { key: 'log', label: 'Medicine log' },
  { key: 'master', label: 'Medicines' },
];

const LOG_COLUMNS = [
  { label: 'No', className: 'tnum', render: (r, i) => i + 1 },
  { label: 'Date', render: (r) => dt(r.date) },
  { label: 'Medicine Log', render: (r) => formatMedicineLog(r.medicine_log) },
  { label: 'Is Archive', render: (r) => yesNo(r.is_archive) },
  { label: 'Created Date Time', render: (r) => dt(r.created_time_stamp) },
];

const MASTER_COLUMNS = [
  { label: 'No', className: 'tnum', render: (r, i) => i + 1 },
  { label: 'Medicine Name', render: (r) => r.medicine_name || '—' },
  { label: 'Start Date', render: (r) => dt(r.start_date) },
  { label: 'End Date', render: (r) => dt(r.end_date) },
  { label: 'Medicine Days', render: (r) => r.medicine_days || '—' },
  { label: 'Medicine Time', render: (r) => r.medicine_time || '—' },
  { label: 'Is Reminder', render: (r) => yesNo(r.is_reminder) },
  { label: 'Remark', render: (r) => r.remark_data || '—' },
  { label: 'Is Archive', render: (r) => yesNo(r.is_archive) },
  { label: 'Created Date Time', render: (r) => dt(r.created_time_stamp) },
];

const MedicineTab = ({ userId, from, to, authGet }) => {
  const [sub, setSub] = useState('log');

  const { status, data, reload } = useTabData(async () => {
    const [logSum, masterSum] = await Promise.all([
      authGet(`/admin/api/medicine-log-summary?${q(userId, from, to)}`),
      authGet(`/admin/api/medicine-master-summary?${q(userId, from, to)}`),
    ]);
    return { log: logSum.data.data || {}, master: masterSum.data.data || {} };
  }, [userId, from, to]);

  if (status === 'loading') return <TabLoading />;
  if (status === 'error') return <TabError onRetry={reload} />;

  const { log, master } = data;
  const anyData = (Number(log.total) || 0) + (Number(master.total) || 0) > 0;
  if (!anyData) {
    return <TabEmpty title="No medicine records" message="This user has no medicine log entries or scheduled medicines in the selected window." icon="💊" />;
  }

  return (
    <div data-testid="ud-medicine">
      <div className="ud-kpis">
        <Kpi label="Log entries" value={num(log.total, 0)} sub={`${num(log.distinct_medicines, 0)} distinct`} />
        <Kpi label="Medicines" value={num(master.total, 0)} sub={`${num(master.active_reminder_count, 0)} reminders on`} />
      </div>

      <h4 className="ud-section-title">Records</h4>
      <SubTabs items={SUBS} active={sub} onSelect={setSub} ariaLabel="Medicine record type" />
      {sub === 'log' ? (
        <DetailList
          key="log"
          userId={userId}
          from={from}
          to={to}
          authGet={authGet}
          endpoint="medicine-log-details"
          testId="ud-medicine-log-list"
          columns={LOG_COLUMNS}
          empty={{ title: 'No medicine log entries', message: 'Nothing logged for this user in the selected window.', icon: '💊' }}
        />
      ) : (
        <DetailList
          key="master"
          userId={userId}
          from={from}
          to={to}
          authGet={authGet}
          endpoint="medicine-master-details"
          testId="ud-medicine-master-list"
          columns={MASTER_COLUMNS}
          empty={{ title: 'No scheduled medicines', message: 'This user has no medicines set up in the selected window.', icon: '💊' }}
        />
      )}
    </div>
  );
};

export default MedicineTab;
